import { useState } from 'react'
import { useForm, useFieldArray } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
    Plus, Loader2, Trash2, UserPlus, Check, X,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
    Sheet,
    SheetContent,
    SheetTitle,
    SheetTrigger,
    SheetClose,
} from '@/components/ui/sheet'
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { useCreatePatient } from '@/hooks/usePatients'
import { useCreateClinicalHistory } from '@/hooks/useClinicalHistory'
import { useToast } from '@/hooks/use-toast'
import { cn } from '@/lib/utils'

const patientSchema = z.object({
    first_name: z.string().min(1, 'El nombre es obligatorio').max(100),
    last_name: z.string().min(1, 'El apellido es obligatorio').max(100),
    date_of_birth: z.string()
        .min(1, 'La fecha de nacimiento es obligatoria')
        .refine((d) => !isNaN(new Date(d).getTime()), 'La fecha de nacimiento no es válida')
        .refine((d) => new Date(d) <= new Date(), 'La fecha de nacimiento no puede ser futura'),
    gender: z.enum(['male', 'female', 'non_binary', 'prefer_not_to_say', 'other']),
    curp: z.string().max(18, 'La CURP tiene máximo 18 caracteres').optional(),
    rfc: z.string().max(13, 'El RFC tiene máximo 13 caracteres').optional(),
    email: z.string().email('Correo electrónico inválido').optional().or(z.literal('')),
    phone_primary: z.string().max(20).optional(),
    blood_type: z.enum(['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-', '']).optional(),
    address_line1: z.string().max(255).optional(),
    city: z.string().max(100).optional(),
    postal_code: z.string().max(20).optional(),
    emergency_contacts: z.array(z.object({
        name: z.string().min(1, 'El nombre del contacto es obligatorio').max(100),
        relationship: z.string().min(1, 'La relación es obligatoria').max(50),
        phone: z.string().min(5, 'El número de teléfono es obligatorio').max(20),
        is_primary: z.boolean().default(true),
    })).min(1, 'Se requiere al menos un contacto de emergencia'),
    consent_to_treatment: z.boolean().refine((v) => v === true, {
        message: 'Debe consentir el tratamiento médico',
    }),
    privacy_consent: z.boolean().refine((v) => v === true, {
        message: 'Debe aceptar el aviso de privacidad y el tratamiento de datos',
    }),
})

type PatientFormValues = z.infer<typeof patientSchema>

// ─── Section header ──────────────────────────────────────────────────────────
function SectionHeader({ label, dotColor = 'bg-ml-primary' }: { label: string; dotColor?: string }) {
    return (
        <div className="flex items-center gap-3 mb-4">
            <span className={cn('h-2 w-2 rounded-full shrink-0', dotColor)} />
            <span className="text-[10px] font-bold uppercase tracking-widest text-ml-on-surface-variant whitespace-nowrap">
                {label}
            </span>
            <div className="flex-1 h-px bg-ml-outline-variant/40" />
        </div>
    )
}

// ─── Consent checkbox card ────────────────────────────────────────────────────
function ConsentCheckbox({
    checked,
    onChange,
    error,
    children,
}: {
    checked: boolean
    onChange: (v: boolean) => void
    error?: string
    children: React.ReactNode
}) {
    return (
        <div>
            <label className={cn(
                'flex items-start gap-3.5 cursor-pointer rounded-xl p-4 border-2 transition-all duration-200',
                checked
                    ? 'border-ml-primary bg-ml-primary/5'
                    : 'border-ml-outline-variant/30 bg-white hover:border-ml-outline-variant/60',
            )}>
                <span className={cn(
                    'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 transition-all',
                    checked ? 'bg-ml-primary border-ml-primary' : 'border-ml-outline-variant bg-white',
                )}>
                    {checked && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                </span>
                <input
                    type="checkbox"
                    className="sr-only"
                    checked={checked}
                    onChange={(e) => onChange(e.target.checked)}
                />
                <span className="text-sm text-ml-on-surface leading-relaxed">{children}</span>
            </label>
            {error && <p className="mt-1.5 text-xs font-medium text-ml-error">{error}</p>}
        </div>
    )
}

// ─── Main component ───────────────────────────────────────────────────────────
export function CreatePatientDialog() {
    const [open, setOpen] = useState(false)
    const { toast } = useToast()
    const { mutate: createPatient, isPending: isCreatingPatient } = useCreatePatient()
    const { mutate: createClinicalHistory, isPending: isCreatingHistory } = useCreateClinicalHistory()

    const isPending = isCreatingPatient || isCreatingHistory

    const form = useForm<PatientFormValues>({
        resolver: zodResolver(patientSchema),
        defaultValues: {
            first_name: '',
            last_name: '',
            gender: 'prefer_not_to_say',
            email: '',
            phone_primary: '',
            blood_type: '',
            curp: '',
            rfc: '',
            address_line1: '',
            city: '',
            postal_code: '',
            emergency_contacts: [{ name: '', relationship: '', phone: '', is_primary: true }],
            consent_to_treatment: false,
            privacy_consent: false,
        },
    })

    const { fields, append, remove } = useFieldArray({
        control: form.control,
        name: 'emergency_contacts',
    })

    const handleClose = () => {
        setOpen(false)
        form.reset()
    }

    function onSubmit(data: PatientFormValues) {
        const { privacy_consent, ...rest } = data
        const payload: Record<string, unknown> = {
            ...rest,
            status: 'active',
            privacy_notice_acknowledged: privacy_consent,
            data_processing_consent: privacy_consent,
        }
        for (const key of ['email', 'phone_primary', 'blood_type', 'address_line1', 'city', 'postal_code', 'curp', 'rfc']) {
            if (!payload[key]) delete payload[key]
        }
        createPatient(payload as unknown as PatientFormValues, {
            onSuccess: (patient) => {
                createClinicalHistory(
                    { patientId: patient.id },
                    {
                        onSuccess: () => {
                            handleClose()
                            toast({
                                title: 'Paciente registrado',
                                description: 'El expediente clínico ha sido abierto exitosamente.',
                            })
                        },
                        onError: () => {
                            handleClose()
                            toast({
                                title: 'Paciente registrado',
                                description: 'El paciente fue registrado. Un médico debe crear y firmar la historia clínica.',
                            })
                        },
                    },
                )
            },
            onError: (error) => {
                toast({
                    title: 'Error de registro',
                    description: error.message || 'Hubo un error al registrar al paciente.',
                    variant: 'destructive',
                })
            },
        })
    }

    return (
        <Sheet open={open} onOpenChange={(v) => { if (!v) handleClose(); else setOpen(true) }}>
            <SheetTrigger asChild>
                <Button className="bg-ml-primary hover:bg-ml-primary-container text-white transition-colors shadow-sm">
                    <Plus className="mr-2 h-4 w-4" />
                    Registrar Paciente
                </Button>
            </SheetTrigger>

            <SheetContent
                side="right"
                className="sm:max-w-[640px] p-0 flex flex-col h-full gap-0 [&>button]:hidden"
            >
                {/* ── Sticky dark-green header ─────────────────────────── */}
                <div className="bg-ml-primary shrink-0 px-7 py-6 relative flex items-center gap-4">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/15 backdrop-blur-sm">
                        <UserPlus className="h-5 w-5 text-white" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <SheetTitle className="text-white font-headline text-xl italic leading-tight">
                            Nuevo Paciente
                        </SheetTitle>
                        <p className="text-white/70 text-xs mt-0.5">
                            Complete los datos para crear el expediente clínico
                        </p>
                    </div>
                    <SheetClose asChild>
                        <button
                            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/10 hover:bg-white/20 transition-colors text-white"
                            aria-label="Cerrar"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </SheetClose>
                </div>

                {/* ── Scrollable body ──────────────────────────────────── */}
                <Form {...form}>
                    <form
                        onSubmit={form.handleSubmit(onSubmit)}
                        className="flex flex-col flex-1 min-h-0"
                    >
                        <div className="flex-1 overflow-y-auto px-7 py-6 space-y-8">

                            {/* ── Identidad ── */}
                            <section>
                                <SectionHeader label="Identidad" />
                                <div className="space-y-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        <FormField
                                            control={form.control}
                                            name="first_name"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel className="ml-field-label">Nombre</FormLabel>
                                                    <FormControl>
                                                        <Input placeholder="Juan" {...field} />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                        <FormField
                                            control={form.control}
                                            name="last_name"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel className="ml-field-label">Apellidos</FormLabel>
                                                    <FormControl>
                                                        <Input placeholder="García López" {...field} />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <FormField
                                            control={form.control}
                                            name="date_of_birth"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel className="ml-field-label">Fecha de Nacimiento</FormLabel>
                                                    <FormControl>
                                                        <Input type="date" lang="es-MX" {...field} />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                        <FormField
                                            control={form.control}
                                            name="gender"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel className="ml-field-label">Género</FormLabel>
                                                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                                                        <FormControl>
                                                            <SelectTrigger>
                                                                <SelectValue placeholder="Seleccionar género" />
                                                            </SelectTrigger>
                                                        </FormControl>
                                                        <SelectContent>
                                                            <SelectItem value="male">Masculino</SelectItem>
                                                            <SelectItem value="female">Femenino</SelectItem>
                                                            <SelectItem value="non_binary">No binario</SelectItem>
                                                            <SelectItem value="prefer_not_to_say">Prefiero no indicarlo</SelectItem>
                                                            <SelectItem value="other">Otro</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <FormField
                                            control={form.control}
                                            name="curp"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel className="ml-field-label">CURP</FormLabel>
                                                    <FormControl>
                                                        <Input
                                                            placeholder="18 caracteres"
                                                            maxLength={18}
                                                            className="font-mono uppercase"
                                                            {...field}
                                                            onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                                                        />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                        <FormField
                                            control={form.control}
                                            name="rfc"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel className="ml-field-label">RFC</FormLabel>
                                                    <FormControl>
                                                        <Input
                                                            placeholder="13 caracteres"
                                                            maxLength={13}
                                                            className="font-mono uppercase"
                                                            {...field}
                                                            onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                                                        />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    </div>
                                </div>
                            </section>

                            {/* ── Contacto ── */}
                            <section>
                                <SectionHeader label="Contacto" />
                                <div className="grid grid-cols-2 gap-4">
                                    <FormField
                                        control={form.control}
                                        name="email"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel className="ml-field-label">Correo Electrónico</FormLabel>
                                                <FormControl>
                                                    <Input type="email" placeholder="juan@ejemplo.com" {...field} />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                    <FormField
                                        control={form.control}
                                        name="phone_primary"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel className="ml-field-label">Teléfono</FormLabel>
                                                <FormControl>
                                                    <Input placeholder="+52 55 0000 0000" {...field} />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>
                            </section>

                            {/* ── Domicilio ── */}
                            <section>
                                <SectionHeader label="Domicilio" />
                                <div className="space-y-4">
                                    <FormField
                                        control={form.control}
                                        name="address_line1"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel className="ml-field-label">Dirección</FormLabel>
                                                <FormControl>
                                                    <Input
                                                        placeholder="Calle, número exterior e interior, colonia"
                                                        {...field}
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                    <div className="grid grid-cols-2 gap-4">
                                        <FormField
                                            control={form.control}
                                            name="city"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel className="ml-field-label">Ciudad / Municipio</FormLabel>
                                                    <FormControl>
                                                        <Input placeholder="Ciudad de México" {...field} />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                        <FormField
                                            control={form.control}
                                            name="postal_code"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel className="ml-field-label">Código Postal</FormLabel>
                                                    <FormControl>
                                                        <Input placeholder="06600" maxLength={6} {...field} />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    </div>
                                </div>
                            </section>

                            {/* ── Datos Clínicos ── */}
                            <section>
                                <SectionHeader label="Datos Clínicos" dotColor="bg-ml-tertiary" />
                                <FormField
                                    control={form.control}
                                    name="blood_type"
                                    render={({ field }) => (
                                        <FormItem className="max-w-[200px]">
                                            <FormLabel className="ml-field-label">Tipo de Sangre</FormLabel>
                                            <Select
                                                onValueChange={(v) => field.onChange(v === '__unknown__' ? '' : v)}
                                                value={field.value || '__unknown__'}
                                            >
                                                <FormControl>
                                                    <SelectTrigger>
                                                        <SelectValue placeholder="Seleccionar" />
                                                    </SelectTrigger>
                                                </FormControl>
                                                <SelectContent>
                                                    <SelectItem value="__unknown__">Desconocido</SelectItem>
                                                    <SelectItem value="A+">A+</SelectItem>
                                                    <SelectItem value="A-">A−</SelectItem>
                                                    <SelectItem value="B+">B+</SelectItem>
                                                    <SelectItem value="B-">B−</SelectItem>
                                                    <SelectItem value="AB+">AB+</SelectItem>
                                                    <SelectItem value="AB-">AB−</SelectItem>
                                                    <SelectItem value="O+">O+</SelectItem>
                                                    <SelectItem value="O-">O−</SelectItem>
                                                </SelectContent>
                                            </Select>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </section>

                            {/* ── Contacto de Emergencia ── */}
                            <section>
                                <SectionHeader label="Contacto de Emergencia" />
                                <div className="space-y-3">
                                    {fields.map((fieldItem, index) => (
                                        <div
                                            key={fieldItem.id}
                                            className="rounded-xl border border-ml-outline-variant/30 bg-ml-surface-container-lowest p-4"
                                        >
                                            <div className="flex items-center justify-between mb-3">
                                                <span className="text-[10px] font-bold uppercase tracking-widest text-ml-on-surface-variant">
                                                    Contacto {index + 1}{index === 0 ? ' · Principal' : ''}
                                                </span>
                                                {fields.length > 1 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => remove(index)}
                                                        className="flex h-7 w-7 items-center justify-center rounded-lg text-ml-on-surface-variant/60 hover:bg-ml-error-container hover:text-ml-error transition-colors"
                                                    >
                                                        <Trash2 className="h-3.5 w-3.5" />
                                                    </button>
                                                )}
                                            </div>
                                            <div className="grid grid-cols-3 gap-3">
                                                <FormField
                                                    control={form.control}
                                                    name={`emergency_contacts.${index}.name`}
                                                    render={({ field }) => (
                                                        <FormItem>
                                                            <FormLabel className="ml-field-label">Nombre</FormLabel>
                                                            <FormControl>
                                                                <Input placeholder="Nombre completo" {...field} />
                                                            </FormControl>
                                                            <FormMessage />
                                                        </FormItem>
                                                    )}
                                                />
                                                <FormField
                                                    control={form.control}
                                                    name={`emergency_contacts.${index}.relationship`}
                                                    render={({ field }) => (
                                                        <FormItem>
                                                            <FormLabel className="ml-field-label">Parentesco</FormLabel>
                                                            <FormControl>
                                                                <Input placeholder="ej. Cónyuge" {...field} />
                                                            </FormControl>
                                                            <FormMessage />
                                                        </FormItem>
                                                    )}
                                                />
                                                <FormField
                                                    control={form.control}
                                                    name={`emergency_contacts.${index}.phone`}
                                                    render={({ field }) => (
                                                        <FormItem>
                                                            <FormLabel className="ml-field-label">Teléfono</FormLabel>
                                                            <FormControl>
                                                                <Input placeholder="+52 55 0000 0000" {...field} />
                                                            </FormControl>
                                                            <FormMessage />
                                                        </FormItem>
                                                    )}
                                                />
                                            </div>
                                        </div>
                                    ))}

                                    {form.formState.errors.emergency_contacts?.root && (
                                        <p className="text-xs font-medium text-ml-error">
                                            {form.formState.errors.emergency_contacts.root.message}
                                        </p>
                                    )}

                                    <button
                                        type="button"
                                        onClick={() => append({ name: '', relationship: '', phone: '', is_primary: false })}
                                        className="flex items-center gap-2 text-xs font-bold text-ml-primary/80 hover:text-ml-primary uppercase tracking-wider transition-colors mt-1"
                                    >
                                        <span className="flex h-6 w-6 items-center justify-center rounded border-2 border-dashed border-ml-primary/40 hover:border-ml-primary transition-colors">
                                            <Plus className="h-3 w-3" />
                                        </span>
                                        Agregar otro contacto
                                    </button>
                                </div>
                            </section>

                            {/* ── Consentimiento ── */}
                            <section>
                                <SectionHeader label="Consentimiento" dotColor="bg-ml-secondary" />
                                <p className="text-xs text-ml-on-surface-variant leading-relaxed mb-4">
                                    Antes de registrar al paciente, confirma que ha otorgado los siguientes consentimientos
                                    en cumplimiento con la NOM-004-SSA3 y la LFPDPPP.
                                </p>
                                <div className="space-y-3">
                                    <FormField
                                        control={form.control}
                                        name="consent_to_treatment"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormControl>
                                                    <ConsentCheckbox
                                                        checked={field.value}
                                                        onChange={field.onChange}
                                                        error={form.formState.errors.consent_to_treatment?.message}
                                                    >
                                                        El paciente ha sido informado sobre el proceso de atención médica y
                                                        otorga su consentimiento para recibir tratamiento en este establecimiento
                                                        de salud.
                                                    </ConsentCheckbox>
                                                </FormControl>
                                            </FormItem>
                                        )}
                                    />
                                    <FormField
                                        control={form.control}
                                        name="privacy_consent"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormControl>
                                                    <ConsentCheckbox
                                                        checked={field.value}
                                                        onChange={field.onChange}
                                                        error={form.formState.errors.privacy_consent?.message}
                                                    >
                                                        El paciente ha leído el{' '}
                                                        <a
                                                            href="/aviso-de-privacidad"
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            className="text-ml-primary underline underline-offset-2 font-medium"
                                                        >
                                                            aviso de privacidad
                                                        </a>{' '}
                                                        y consiente el tratamiento de sus datos personales sensibles conforme
                                                        a la LFPDPPP.
                                                    </ConsentCheckbox>
                                                </FormControl>
                                            </FormItem>
                                        )}
                                    />
                                </div>
                            </section>

                        </div>

                        {/* ── Sticky footer ────────────────────────────────── */}
                        <div className="shrink-0 flex items-center justify-between border-t border-ml-outline-variant/15 bg-ml-surface-container-lowest px-7 py-4">
                            <Button
                                type="button"
                                variant="ghost"
                                className="text-ml-on-surface-variant hover:text-ml-on-surface rounded"
                                onClick={handleClose}
                            >
                                Cancelar
                            </Button>

                            <Button
                                type="submit"
                                className="bg-ml-primary hover:bg-ml-primary-container text-white transition-colors rounded px-6 shadow-sm"
                                disabled={isPending}
                            >
                                {isPending ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Registrando…
                                    </>
                                ) : (
                                    <>
                                        Registrar Paciente
                                        <Check className="ml-2 h-4 w-4" />
                                    </>
                                )}
                            </Button>
                        </div>
                    </form>
                </Form>
            </SheetContent>
        </Sheet>
    )
}
